import { useEffect, useId, useRef, type JSX, type KeyboardEvent, type ReactNode } from 'react';

import styles from './Modal.module.scss';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Contenido extra en la cabecera (p. ej. indicador de pasos). */
  headerExtra?: ReactNode;
  /**
   * Cierre por Esc, clic fuera y botón ✕. Sin él, el modal solo se cierra desde sus propios
   * botones: para contenido que no se puede perder (p. ej. credenciales que no se vuelven a mostrar).
   */
  onRequestClose?: () => void;
  /** Etiqueta del botón ✕ (lectores de pantalla). */
  closeLabel?: string;
}

/**
 * OS-1712 — modal accesible de la consola: `role="dialog"`, `aria-modal`, título enlazado, foco
 * inicial dentro del diálogo, trampa de foco con Tab/Shift+Tab y devolución del foco al cerrar.
 */
export function Modal({
  title,
  children,
  footer,
  headerExtra,
  onRequestClose,
  closeLabel = 'Cerrar',
}: ModalProps): JSX.Element {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onRequestClose);
  closeRef.current = onRequestClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    // Primero el elemento marcado, luego el contenido (no el ✕ de la cabecera).
    const target =
      dialog?.querySelector<HTMLElement>('[data-autofocus]') ??
      bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      dialog?.querySelector<HTMLElement>(FOCUSABLE) ??
      dialog;
    target?.focus();
    return (): void => {
      previouslyFocused?.focus?.();
    };
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeRef.current?.();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) {
      return;
    }
    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  return (
    <div
      className={styles.overlay}
      data-testid="modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          closeRef.current?.();
        }
      }}
    >
      <div
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.header}>
          <h2 id={titleId}>{title}</h2>
          {headerExtra}
          {onRequestClose ? (
            <button
              className={styles.closeBtn}
              type="button"
              aria-label={closeLabel}
              onClick={onRequestClose}
            >
              ✕
            </button>
          ) : null}
        </div>
        <div ref={bodyRef} className={styles.body}>
          {children}
        </div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  );
}
